import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, FileText, Map, TrendingUp,
  CheckCircle, ArrowRight, Play, Star, Clock,
  DollarSign, BarChart3, Zap, Award, MessageSquare,
  ChevronRight, Menu, X
} from 'lucide-react';
import SEO from '../components/SEO';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      id: 'site-analysis',
      icon: Map,
      title: 'AI-Powered Site Compliance Check',
      description: 'Live County GIS Data Feed for terrain mapping, verified setback calculations, and instant zoning compliance verification'
    },
    {
      id: 'floor-plans-cad',
      icon: FileText,
      title: 'Floor Plans & CAD',
      description: 'Upload CAD files (DWG, DXF, PDF) and selections - AI automatically detects rooms, places selections in correct locations, with real-time 3D visualization'
    },
    {
      id: 'ai-communications',
      icon: MessageSquare,
      title: 'AI-Powered Project Communications',
      description: '24/7 AI assistant answers calls, schedules appointments, transfers to team members, composes emails with proper context, and replies to messages across all platforms'
    },
    {
      id: 'vendor-bidding',
      icon: Users,
      title: 'Smart Vendor Bidding',
      description: 'Send bid requests with embedded CAD files and all project specs, compare line items side-by-side, with performance-based vendor recommendations from historical data'
    },
    {
      id: 'phases-budget',
      icon: BarChart3,
      title: 'Smart Phases & Budget',
      description: 'Track budgets across phases, AI categorizes invoices, monitors timelines, and auto-schedules vendors based on lead times - with AI message and call confirmations'
    },
    {
      id: 'ai-photo-notes',
      icon: Building2,
      title: 'AI-Powered Site Progress Logging',
      description: 'Take site photos and get instant detailed notes, AI issue detection (defects, safety concerns), progress tracking with before/after comparisons, and smart organization by room'
    }
  ];

  const benefits = [
    { label: 'Time saved on admin tasks', value: 'Up to 40%' },
    { label: 'Reduce project delays', value: 'Target' },
    { label: 'Increase profit margins', value: 'Goal' },
    { label: 'Beta participants', value: 'Growing' }
  ];

  const testimonials = [
    {
      name: 'Michael Chen',
      role: 'General Contractor, Apex Construction',
      content: 'HomeQuest transformed how we manage projects. What used to take hours now takes minutes. Our clients love the transparency.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      role: 'Project Manager, BuildRight Inc',
      content: 'The automated site compliance check saved us thousands on our last project. The ROI was immediate.',
      rating: 5
    },
    {
      name: 'David Martinez',
      role: 'CEO, Modern Homes LLC',
      content: 'Finally, a platform that understands construction. Easy to use, powerful features, and amazing support.',
      rating: 5
    }
  ];

  return (
    <div className="landing-page">
      <SEO
        title="HomeQuest Tech - AI-Powered Construction Management Platform"
        description="Transform your construction business with AI-powered project management. Get instant site compliance with live county GIS data, automated communications, smart vendor bidding, and real-time collaboration. Start your 2-week free trial today."
        keywords="construction management software, AI construction platform, project management, site compliance, GIS analysis, contractor software, builder app, construction automation, AI-powered construction, live county data, zoning compliance"
        canonical="/"
      />
      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`landing-nav ${scrolled ? 'scrolled' : ''}`}
      >
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>

          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#benefits" onClick={() => setMobileMenuOpen(false)}>Benefits</a>
            <a href="#" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/pricing'); }}>Pricing</a>
            <a href="#testimonials" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
            <button onClick={() => { setMobileMenuOpen(false); navigate('/login'); }} className="nav-login">
              Sign In
            </button>
            <button onClick={() => { setMobileMenuOpen(false); navigate('/pricing'); }} className="nav-cta">
              Start Free Trial
            </button>
          </div>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero-content"
          >
            <div className="hero-badge">
              <Zap className="badge-icon" />
              <span>🔥 ONLY 3 BETA SPOTS LEFT • LOCK IN 30% OFF FOR LIFE</span>
            </div>

            <h1 className="hero-title">
              Where Land
              <span className="gradient-text"> Meets Logic</span>
            </h1>

            <p className="hero-tagline">Instant Site Compliance From Live County Data</p>

            <p className="hero-subtitle">
              Stop wasting hours on manual site research. The only construction platform with Live County GIS Data Feed for instant setback calculations and verified zoning compliance. Trusted by builders to deliver projects on time and under budget. Only 3 founding member spots remaining.
            </p>

            <div className="hero-cta-group">
              <button
                onClick={() => navigate('/pricing')}
                className="cta-primary"
              >
                Join Beta - Lock in 30% OFF Forever
                <ArrowRight className="cta-icon" />
              </button>
              <button
                onClick={() => navigate('/demo')}
                className="cta-secondary"
              >
                <Play className="play-icon" />
                Try Interactive Demo
              </button>
            </div>

            <div className="hero-trust">
              <div className="trust-stats">
                <div className="trust-stat">
                  <Star className="star-icon" />
                  <span><strong>Beta</strong> Program</span>
                </div>
                <div className="trust-stat">
                  <Users className="stat-icon" />
                  <span><strong>Early</strong> Access</span>
                </div>
                <div className="trust-stat">
                  <Award className="stat-icon" />
                  <span><strong>$5M+</strong> projects managed</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-image"
          >
            <div className="dashboard-preview">
              <div className="preview-header">
                <div className="preview-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="preview-title">Maple Street Residence - Live Project</div>
              </div>
              <div className="preview-content">
                <div className="preview-card">
                  <TrendingUp className="preview-icon" />
                  <div>
                    <div className="preview-label">Project Progress</div>
                    <div className="preview-value">68%</div>
                  </div>
                </div>
                <div className="preview-card">
                  <Clock className="preview-icon" />
                  <div>
                    <div className="preview-label">Days Ahead</div>
                    <div className="preview-value">+5</div>
                  </div>
                </div>
                <div className="preview-card">
                  <DollarSign className="preview-icon" />
                  <div>
                    <div className="preview-label">Under Budget</div>
                    <div className="preview-value">$12K</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* See It In Action Section - Video Background */}
      <section className="action-section">
        <video autoPlay loop muted playsInline className="action-video-bg">
          <source src="/images/8965303-uhd_3840_2160_25fps.mp4" type="video/mp4" />
        </video>
        <div className="action-overlay"></div>
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="action-content"
          >
            <h2 className="action-title">AI-Powered Site Compliance Check</h2>
            <p className="action-subtitle">
              Stop wasting hours on manual site research. Get instant compliance verification with live county data—accurate, verified, and permit-ready.
            </p>
            <div className="action-features">
              <div className="action-feature-item">
                <CheckCircle className="action-check-icon" />
                <span>Live County GIS Data Feed</span>
              </div>
              <div className="action-feature-item">
                <CheckCircle className="action-check-icon" />
                <span>Verified setback calculations</span>
              </div>
              <div className="action-feature-item">
                <CheckCircle className="action-check-icon" />
                <span>Instant compliance verification</span>
              </div>
              <div className="action-feature-item">
                <CheckCircle className="action-check-icon" />
                <span>3D terrain visualization</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/demo')}
              className="cta-primary"
            >
              Try Interactive Demo
              <ArrowRight className="cta-icon" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Everything you need to build successfully</h2>
            <p className="section-subtitle">
              AI-powered tools designed specifically for construction professionals—from Live County Data to intelligent project management
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="feature-card"
              >
                <div className="feature-icon">
                  <feature.icon />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/features#${feature.id}`);
                  }}
                  className="feature-link"
                  style={{ cursor: 'pointer' }}
                >
                  Learn more <ChevronRight className="link-icon" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="benefits-section">
        <div className="section-container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2 className="section-title">Built for results</h2>
              <p className="section-subtitle">
                See measurable improvements in your construction business from day one
              </p>

              <div className="benefits-list">
                <div className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>AI-powered scheduling and task management</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>Real-time project tracking and updates</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>Integrated vendor and subcontractor management</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>AI-powered cost tracking and budget alerts</span>
                </div>
              </div>

              <button
                onClick={() => navigate('/pricing')}
                className="cta-primary"
              >
                Lock in Beta Pricing Now
                <ArrowRight className="cta-icon" />
              </button>
            </div>

            <div className="benefits-stats">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="stat-card"
                >
                  <div className="stat-value">{benefit.value}</div>
                  <div className="stat-label">{benefit.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Early adopters love it</h2>
            <p className="section-subtitle">
              See what our beta users are saying
            </p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="testimonial-card"
              >
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="star-filled" />
                  ))}
                </div>
                <p className="testimonial-content">{testimonial.content}</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bible Verse Section */}
      <section className="bible-verse-section">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bible-verse-content"
          >
            <p className="verse-text">
              "Unless the Lord builds the house, the builders labor in vain."
            </p>
            <p className="verse-reference">— Psalm 127:1</p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2 className="cta-title">Only 3 Founding Member Spots Left</h2>
            <p className="cta-subtitle">
              Get instant site compliance with Live County GIS Data Feed. Lock in 30% off for life before spots run out.
            </p>
            <div className="cta-buttons">
              <button
                onClick={() => navigate('/pricing')}
                className="cta-primary large"
              >
                Claim Your Spot - 30% OFF Forever
                <ArrowRight className="cta-icon" />
              </button>
              <button
                onClick={() => navigate('/demo')}
                className="cta-secondary large"
              >
                Try Demo First
              </button>
            </div>
            <p className="cta-note">
              <CheckCircle className="check-small" />
              2-week free trial • Live County Data • Only 3 spots remaining
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-column">
              <div className="footer-logo">
                <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">Where Land Meets Logic</p>
              <p className="footer-description">
                The modern construction management platform built for builders who demand excellence.
              </p>
            </div>

            <div className="footer-column">
              <h4>Product</h4>
              <a onClick={(e) => { e.preventDefault(); navigate('/features'); }} style={{ cursor: 'pointer' }}>Features</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} style={{ cursor: 'pointer' }}>Pricing</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/docs'); }} style={{ cursor: 'pointer' }}>API</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/status'); }} style={{ cursor: 'pointer' }}>Status</a>
            </div>

            <div className="footer-column">
              <h4>Company</h4>
              <a onClick={(e) => { e.preventDefault(); navigate('/about'); }} style={{ cursor: 'pointer' }}>About</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/blog'); }} style={{ cursor: 'pointer' }}>Blog</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/contact'); }} style={{ cursor: 'pointer' }}>Contact</a>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              <a onClick={(e) => { e.preventDefault(); navigate('/help'); }} style={{ cursor: 'pointer' }}>Help Center</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/docs'); }} style={{ cursor: 'pointer' }}>Documentation</a>
              <a href="mailto:support@homequesttech.com" style={{ cursor: 'pointer' }}>Email Us</a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 HomeQuest Tech™. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={(e) => { e.preventDefault(); navigate('/privacy'); }} style={{ cursor: 'pointer' }}>Privacy</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/terms'); }} style={{ cursor: 'pointer' }}>Terms</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/security'); }} style={{ cursor: 'pointer' }}>Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
